<script setup lang="ts">
import { api } from '@/api';
import {
  type CreateUpdateUserDto,
  CreateUpdateUserDtoTypeEnum,
  UserStateOfUse,
} from '@/api/generator';
import VeeSubmitButton from '@/components/forms/SubmitButton/VeeSubmitButton.vue';
import { useVeeForm } from '@/components/forms/useVeeForm';
import BasePage from '@/components/pages/BasePage/BasePage.vue';
import BasePageStatus from '@/components/pages/BasePage/BasePageStatus.vue';
import BasePageTitle from '@/components/pages/BasePage/BasePageTitle.vue';
import PtwCancelButton from '@/components/buttons/PtwCancelButton.vue';
import { formBackendErrorHandler } from '@/errorHandling';
import { routenames } from '@/router/routenames';
import { useNotificationStore } from '@/stores/notificationStore';
import { useRouterStore } from '@/stores/routerStore';
import { fullName, fullNameWithIuid } from '@/utils/helper';
import { useTranslation } from 'i18next-vue';
import type { PartialDeep } from 'type-fest';
import { useRouter } from 'vue-router';
import AccountForm from './components/AccountForm.vue';
import { backendValidator } from '@/validation/validators/backendValidator';

const { t } = useTranslation();

const notificationStore = useNotificationStore();
const router = useRouter();

const initialValues: PartialDeep<CreateUpdateUserDto> = {
  isAvailable: true,
  lastName: t('Untitled'),
  state: UserStateOfUse.InAdding,
  $type:
    CreateUpdateUserDtoTypeEnum.PtwPtw100AppZeroApplicationSharedAuthorizationUsersDtoCreateUpdateUserDtoPtwPtw100AppZeroApplicationShared,
};

const { form } = useVeeForm<CreateUpdateUserDto>({
  schemaName: 'CreateUpdateUserDto',
  options: {
    initialValues,
  },
  mode: 'add',
  customValidateFunctions: [
    backendValidator((userDto, errorHandlers) =>
      api.usersApi.validateUser(
        {
          createUpdateUserDto: {
            ...userDto,
            $type:
              CreateUpdateUserDtoTypeEnum.PtwPtw100AppZeroApplicationSharedAuthorizationUsersDtoCreateUpdateUserDtoPtwPtw100AppZeroApplicationShared,
          },
        },
        errorHandlers,
      ),
    ),
  ],
});

const { handleSubmit, values } = form;

const submit = handleSubmit(async (createUpdateUserDto) => {
  const { result } = await api.usersApi.createUser(
    {
      createUpdateUserDto,
    },
    [formBackendErrorHandler(form)],
  );
  if (result) {
    notificationStore.success(
      t('AccountAddedSuccessfullyMsg', {
        userFullNameWithIuid: fullNameWithIuid(result),
      }),
    );
    useRouterStore().userAdded = result;
    await router.push({
      name: routenames.accounts.overview,
    });
  }
});
</script>

<template>
  <form @submit="submit">
    <BasePage>
      <template #title>
        <BasePageTitle :subtitle="$t('UserAccount_one')" :title="fullName(values)" />
      </template>

      <template #status>
        <BasePageStatus :entity="values" />
      </template>

      <template #actions>
        <PtwCancelButton :to="{ name: routenames.accounts.overview }" />
        <VeeSubmitButton class="ml-4" :ignoreDirty="true" :text="$t('SaveAndClose')" />
      </template>

      <AccountForm />
    </BasePage>
  </form>
</template>
